import { CSSProperties } from "react";

import { colors } from "@dotnet/telematics-design-system";

import { CELL_ROLE, StripParams } from "../../../shared/types";

const CELL_WIDTH = 44;
const BUBBLE_WIDTH = 32;

const CELL_HALF = CELL_WIDTH / 2;
const BUBBLE_HALF = BUBBLE_WIDTH / 2;

const getBaseStyle = (
  fill: string,
  visible: boolean,
  isPreview: boolean
): CSSProperties => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  background: fill,
  zIndex: 0,
  pointerEvents: "none",
  opacity: visible ? 1 : 0,
  transition: isPreview
    ? "opacity 0ms"
    : "opacity 220ms cubic-bezier(0.4, 0, 0.2, 1)"
});

const getStripPositionStyle = (role: CELL_ROLE): CSSProperties => {
  switch (role) {
    case CELL_ROLE.Start:
      return {
        left: CELL_HALF + BUBBLE_HALF,
        right: 0
      };

    case CELL_ROLE.End:
      return {
        left: 0,
        right: CELL_HALF + BUBBLE_HALF
      };

    case CELL_ROLE.Middle:
      return {
        left: 0,
        right: 0
      };

    default:
      return {};
  }
};

const getCellRole = (
  dayKey: number,
  rangeStart: number,
  rangeEnd: number
): CELL_ROLE => {
  const start = Math.min(rangeStart, rangeEnd);
  const end = Math.max(rangeStart, rangeEnd);

  if (dayKey === start) return CELL_ROLE.Start;
  if (dayKey === end) return CELL_ROLE.End;
  if (dayKey > start && dayKey < end) return CELL_ROLE.Middle;

  return CELL_ROLE.None;
};

export const useDayStripStyle = () => {
  const stripStyle = (dayKey: number, params: StripParams): CSSProperties => {
    const { rangeStart, rangeEnd, isPreview } = params;

    const startOrEndNull = rangeStart === null || rangeEnd === null;
    const isSameSelected = rangeStart !== rangeEnd;

    if (startOrEndNull || !isSameSelected) {
      return getBaseStyle(colors.blue700, false, isPreview);
    }

    const role = getCellRole(dayKey, rangeStart, rangeEnd);

    if (role === CELL_ROLE.None) {
      return getBaseStyle(colors.blue700, false, isPreview);
    }

    const fill = isPreview ? colors.gray100 : colors.blue700;

    return {
      ...getBaseStyle(fill, true, isPreview),
      ...getStripPositionStyle(role)
    };
  };

  return { stripStyle };
};
